import React, { useState, useEffect } from 'react';
import { FiFileText, FiActivity, FiUsers } from 'react-icons/fi';
import { api } from '@crm/shared';
import { C, STAGE_CFG } from '../shared/tokens';
import { Sh, Kpi, Err, Empty } from '../shared/ui';
import { exportCsv, exportExcel, ExportButton } from '../shared/ExportButton';
import { monthLabel } from '../shared/MisPeriodBar';

export default function LoginMIS({ filters = {}, loadTrigger = 0, setLoading, searched }) {
  const { year, month, selUser, dateFrom, dateTo, location, users = [] } = filters;

  const [allRows,   setAllRows]   = useState([]);
  const [localLoad, setLocalLoad] = useState(false);
  const [error,     setError]     = useState(null);
  const [exporting, setExporting] = useState(false);

  const label = monthLabel(year, month);
  const hasDateRange = Boolean(dateFrom && dateTo);
  const periodLabel = hasDateRange ? `${dateFrom} to ${dateTo}` : label;

  const load = async () => {
    if (!year || !month) return;
    setLocalLoad(true); setLoading?.(true); setError(null);
    try {
      const res = await api.getMisLoginMIS({
        year,
        month,
        ...(selUser ? { user: selUser } : {}),
        ...(hasDateRange ? { from_date: dateFrom, to_date: dateTo } : {}),
      });
      const d = res?.data;
      setAllRows(Array.isArray(d?.results) ? d.results : []);
    } catch (e) {
      const msg = e?.response?.status === 401 
        ? 'Authentication failed. Please login again.'
        : e?.response?.status === 403 
        ? 'You do not have permission to view this data.'
        : e?.response?.data?.detail || e?.response?.data?.message || e?.detail || e?.message || e?.toString() || 'Failed to load Login MIS';
      setError(msg);
    }
    finally { setLocalLoad(false); setLoading?.(false); }
  };

  useEffect(() => { if (loadTrigger > 0) load(); }, [loadTrigger]);

  // Client-side filtering
  const rows = allRows.filter(r => {
    if (selUser) {
      const u = users.find(u => String(u.id) === String(selUser));
      const uName = u ? [u.first_name, u.last_name].filter(Boolean).join(' ') : '';
      if (uName && !(r.created_by_name || '').toLowerCase().includes(uName.toLowerCase())) return false;
    }
    if (hasDateRange) {
      const rowDate = r.login_date || '';
      if (rowDate < dateFrom || rowDate > dateTo) return false;
    }
    if (location?.trim()) {
      if (!(r.location || '').toLowerCase().includes(location.trim().toLowerCase())) return false;
    }
    return true;
  });

  const COLS = ['Lead No','Customer','Mobile','Product','Institution','Channel','Banker','Login Date','App No.','User','Location'];
  const toRow = r => [r.lead_no||`#${r.lead_id}`, r.customer_name||'', r.mobile_number||'', r.product||'', r.institution||'', r.channel||'', r.banker||'', r.login_date||'', r.application_number||'', r.created_by_name||'', r.location||''];
  
  const selUserName = selUser 
    ? (users.find(u => String(u.id) === String(selUser))?.first_name + ' ' + users.find(u => String(u.id) === String(selUser))?.last_name).trim()
    : '';
  const title = selUserName ? `Login MIS - ${selUserName}` : `Login MIS - ${periodLabel}`;
  const fname = selUserName ? `login_mis_${selUserName.replace(/\s+/g, '_')}_${year}_${month}` : `login_mis_${year}_${month}`;

  const handleExport = fmt => {
    setExporting(true);
    const data = rows.map(toRow);
    if (fmt === 'excel') exportExcel(COLS, data, fname, title);
    else exportCsv(COLS, data, fname, title);
    setExporting(false);
  };

  if (!searched) return (
    <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'52px 24px', textAlign:'center' }}>
      <div style={{ width:54, height:54, borderRadius:'50%', background:C.purpleL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
        <FiFileText size={22} style={{ color:C.purple, opacity:0.6 }}/>
      </div>
      <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>Select period and click "Load Report"</div>
      <div style={{ fontSize:12.5, color:C.t3 }}>Login cases will appear here</div>
    </div>
  );

  if (localLoad) return <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:18 }}><div style={{ display:'flex', flexDirection:'column', gap:10 }}>{[0,1,2,3,4].map(i => <Sh key={i} h={48}/>)}</div></div>;

  if (error) return <Err msg={error} onRetry={load}/>;

  return (
    <>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:14 }}>
        <Kpi label="Total Login Cases"   value={rows.length} icon={<FiFileText size={15}/>} iconBg={C.purpleL} iconColor={C.purpleB} accent={C.purpleB} loading={false} sub={periodLabel}/>
        <Kpi label="Unique Institutions" value={new Set(rows.map(r => r.institution).filter(Boolean)).size || '—'} icon={<FiActivity size={15}/>} iconBg={C.navyL} iconColor={C.navy} accent={C.navy} loading={false}/>
        <Kpi label="With Channels"       value={rows.filter(r => r.channel).length || '—'} icon={<FiUsers size={15}/>} iconBg={C.tealL} iconColor={C.tealB} accent={C.tealB} loading={false} sub="THRU logins"/>
      </div>

      {rows.length === 0 ? <Empty label={`No login cases found for ${periodLabel}`}/> : (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
          <div style={{ padding:'11px 18px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <FiFileText size={14} style={{ color:C.purpleB }}/>
              <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>Login MIS — {periodLabel}</span>
              <span style={{ background:C.purpleL, color:C.purpleB, padding:'2px 8px', borderRadius:999, fontSize:10.5, fontWeight:700 }}>{rows.length}</span>
            </div>
            <ExportButton onExport={handleExport} loading={exporting} size="sm"/>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
              <thead>
                <tr style={{ background:'#F7F9FB' }}>
                  {['#','Lead No','Customer','Mobile','Product','Institution','Channel','Banker','Login Date','App No.','User','Location'].map(h => (
                    <th key={h} style={{ padding:'9px 12px', fontSize:10.5, fontWeight:800, color:C.t2, textTransform:'uppercase', letterSpacing:'0.07em', textAlign:'left', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const isLast = idx === rows.length - 1;
                  const sc = STAGE_CFG[r.current_stage] || { bg:C.goldL, color:C.gold };
                  return (
                    <tr key={r.id || idx} style={{ background:idx%2===0?C.white:'#FAFBFC' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F0F0FF'}
                      onMouseLeave={e => e.currentTarget.style.background = idx%2===0?C.white:'#FAFBFC'}>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:11.5, color:C.t4 }}>{idx+1}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}><span style={{ fontSize:12, fontWeight:700, color:C.navy, fontFamily:'monospace' }}>{r.lead_no||`#${r.lead_id}`}</span></td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:13, fontWeight:600, color:C.t1 }}>{r.customer_name||'—'}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3 }}>{r.mobile_number||'—'}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}>{r.product||'—'}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}>{r.institution||'—'}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}>{r.channel||'—'}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}>{r.banker||'—'}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12.5, fontWeight:600, color:C.t1 }}>{r.login_date||'—'}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3, fontFamily:'monospace' }}>{r.application_number||'—'}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12.5, fontWeight:600, color:C.t1 }}>{r.created_by_name||'—'}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}>{r.location||'—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
